import React, { useState } from 'react';
import { Link as RouterLink} from "react-router-dom";
import EntreeLocataireModal from 'components/EntreeLocataireModal';
import PaiementModal from 'components/PaiementModal';
import { fetchWithAuth } from 'utils/fetchWithAuth';
import { formatDate } from "utils/dateFormat";
import {
  TableRow, TableCell, Button, Typography, Link, Tooltip, Chip
} from '@mui/material';
import PaidIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

function LotRow({ lot, sciId, selectedMonth, refreshLot, showSnackbar, hideActions = false }) {
  const locations = lot.locations_du_mois || [];
  const isVacant = !locations || locations.length === 0;
  const isCurrentMonth = (selectedMonth) => {
    const now = new Date();
    const nowMonth = now.toISOString().slice(0, 7);
    return selectedMonth === nowMonth;
  };
  const [openPaiement, setOpenPaiement] = useState(null);
  const [openEntree, setOpenEntree] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  if (isVacant) {
    return (
      <>
        <TableRow
          sx={{ 
            '& .MuiTableCell-root': {
              py: 0.7,
            },
            background: "#eee",
            color: "#999" 
          }}
        >
          <TableCell
            sx={{ 
              fontWeight: 400,
              fontSize: "1.12em",
              borderRight: "2px solid #e0e0e0"
            }}
          > 
            <Chip
              label={lot.nom || lot.id}
              variant="outlined"
              size="small"
              sx={{
                bgcolor: '#f0f4f8', 
                borderColor: '#b2bbc2', 
                color: '#375865', 
                fontWeight: 700,
                fontSize: 15,
                px: 2.2,
                py: 0.7,
                minWidth: 70,
                letterSpacing: 0.6
              }}
            />
          </TableCell>
          {hideActions ? (
            <TableCell colSpan={6}>&nbsp;</TableCell>
          ) : (
            <>
              <TableCell colSpan={5}>&nbsp;</TableCell>
              <TableCell align="right">
                <Tooltip
                  title={
                    isCurrentMonth(selectedMonth)
                      ? "Faire entrer un locataire dans ce lot"
                      : "L'entrée n'est possible que sur le mois courant"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      sx={{ mr: 0.5 }}
                      disabled={!isCurrentMonth(selectedMonth)}
                      onClick={() => setOpenEntree(lot)}
                    >Entrée</Button>
                  </span>
                </Tooltip>
              </TableCell>
            </>
          )}
        </TableRow>
        {openEntree && (
          <EntreeLocataireModal
            lot={openEntree}
            sciId={sciId}
            open={!!openEntree}
            onClose={() => setOpenEntree(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      {locations.map((location, i) => {
        const referent = location.locataire_referent;
        const relanceDisabled =
          !referent?.peut_relancer ||
          location.paye ||
          location.statut_paiement !== "en retard";

        const handleRelance = async () => {
          if (!location) return;
          try {
            const resp = await fetchWithAuth(`${API_URL}/api/relance/${location.id}/`, { method: "POST" });
              if (!resp.ok) {
                showSnackbar("Erreur lors de la relance", "error");
                return;
              }

              showSnackbar("Relance envoyée !", "success");
              if (typeof refreshLot === "function") await refreshLot();

            } catch (e) {
              showSnackbar("Erreur réseau", "error");
            }
        };

        const handleQuittance = async (location) => {
          if (!location) return;
          try {
            const response = await fetchWithAuth(`${API_URL}/api/quittance/${referent.id}/?mois=${selectedMonth}`);

            if (!response.ok) {
              showSnackbar("Erreur lors de la génération de la quittance", "error");
              return
            }

            showSnackbar("Quittance générée et envoyée !", "success");

          } catch (e) {
            showSnackbar("Erreur réseau", "error");
          }
        };

        return (
          <TableRow 
            sx={{ 
              '& .MuiTableCell-root': {
                py: 0.7,
              }
            }} 
            key={location.id || i}
          >
            {/* Première colonne fusionnée si plusieurs locataires */}
            {i === 0 && (
              <TableCell
                rowSpan={locations.length}
                sx={{
                  verticalAlign: "middle",
                  background: "#f7f9f9",
                  fontWeight: 400,
                  fontSize: "1.12em",
                  color: "#375865",
                  borderRight: "2px solid #e0e0e0"
                }}
              >
                <Link
                  component={RouterLink}
                  to={{
                    pathname: `/lots/${lot.id}`,
                    search: selectedMonth ? `?mois=${selectedMonth}` : "",
                  }}
                  sx={{ textDecoration: "none" }}
                >
                  <Chip
                    label={lot.nom || lot.id}
                    variant="outlined"
                    size="small"
                    sx={{
                      bgcolor: '#e3f0ff',
                      borderColor: '#90b4d8',
                      color: '#4576a4',
                      fontWeight: 600,
                      fontSize: 15,
                      px: 2.2,
                      py: 0.7,
                      minWidth: 70,
                      letterSpacing: 0.6
                    }}
                    clickable
                  />
                </Link>
              </TableCell>
            )}
            <TableCell>
              {location.locataires && location.locataires.length > 0 ? (
                location.locataires
                  .slice()
                  .sort((a, b) => {
                    if (referent && a.id === referent.id) return -1;
                    if (referent && b.id === referent.id) return 1;
                    return 0;
                  })
                  .map((loc) => (
                    <div key={loc.id}>
                      <Link
                        component={RouterLink}
                        to={`/locataires/${loc.id}`}
                        sx={{ textDecoration: "none", color: "#1565c0", fontWeight: 500 }}
                      >
                        {loc.prenom} {loc.nom}
                      </Link>
                    </div>
                  ))
              ) : (
                <Typography color="text.secondary">Vacant</Typography>
              )}
            </TableCell>
            <TableCell>
              {formatDate(location.date_entree) || "-"}
            </TableCell>
            <TableCell>
              {location.date_sortie ? formatDate(location.date_sortie) : "En cours"}
            </TableCell>
            <TableCell>
              {(location.montant_du !== undefined && location.loyer !== undefined && location.montant_du < location.loyer) ? (
                <Tooltip title={`Loyer mensuel plein : ${location.loyer} €`}>
                  <span>{`${location.montant_du} €`}</span>
                </Tooltip>
              ) : (
                <span>{location.montant_du !== undefined ? `${location.montant_du} €` : "-"}</span>
              )}
            </TableCell>
            <TableCell>
              <Chip
                label={location.statut_paiement || ""}
                color={
                  location.statut_paiement === "acquitté"
                    ? "success"
                  : location.statut_paiement === "en retard"
                    ? "error"
                  : location.statut_paiement === "dû|partiel"
                    ? "warning"
                  : "default"
                }
                size="small"
                icon={
                  location.paye ? (
                    <PaidIcon fontSize="small" sx={{ color: "inherit", mr: 0.2, ml: 0.5 }}/>
                  ) :(
                    <ErrorIcon fontSize="small" sx={{ color: "inherit", mr: 0.2, ml: 0.5 }}/>
                  )  
                }
              />
            </TableCell>
            {!hideActions && (
              <TableCell>
                <Button variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => setOpenPaiement(location)}
                  disabled={location.paye && location.statut_paiement !== "en retard"}
                  sx={{ mr: 0.5 }}
                >Paiement</Button>
                <Button variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => handleQuittance(location)}
                  disabled={!location.paye}
                  sx={{ mr: 0.5 }}
                >Quittance</Button>
                <Button variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => handleRelance(location)}
                  disabled={relanceDisabled}
                >Relancer</Button>
                {/* Relance possible plus tard */}
                {(!referent?.peut_relancer && referent?.next_possible_relance) && (
                  <Typography color="text.secondary" sx={{ pl: 1, fontSize: "0.97em" }}>
                    Relance possible le {new Date(referent.next_possible_relance).toLocaleDateString("fr-FR")}
                  </Typography>
                )}
              </TableCell>
            )}
          </TableRow>
        );
      })}
      {openPaiement && (
        <PaiementModal
          openPaiement={openPaiement}
          onClose={() => setOpenPaiement(null)}
          lot={lot}
          location={openPaiement}
          refreshLot={refreshLot}
        />
      )}
    </>
  );
}

export default LotRow;